export type SentryInitMetadata = {
    dsn?: string;
    release?: string;
    environment?: string;
    sendDefaultPii: false;
    beforeSend: <T>(event: T) => T;
    beforeSendTransaction: <T>(event: T) => T;
};

const isEnabledBuildFlag = (value: unknown): boolean => value === true || value === 'true';

const readBuildString = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;

    const trimmedValue: string = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : undefined;
};

const getSentryDsn = (): string | undefined => readBuildString(process.env.THEIA_SENTRY_DSN || process.env.SENTRY_DSN);

export const isSentryEnabled = (): boolean => !isEnabledBuildFlag(process.env.THEIA_DISABLE_SENTRY) && !!getSentryDsn();

const redactHomeDirectoryPaths = (value: string): string =>
    value
        .replace(/(\/(?:Users|home)\/)[^/"'\s]+/gi, '$1<redacted>')
        .replace(/([A-Za-z]:[\\/]Users[\\/])[^\\/"'\s]+/gi, '$1<redacted>');

const scrubValue = (value: unknown): unknown => {
    if (typeof value === 'string') return redactHomeDirectoryPaths(value);
    if (!value || typeof value !== 'object') return value;

    if (Array.isArray(value)) {
        for (let index = 0; index < value.length; index += 1) {
            value[index] = scrubValue(value[index]);
        }
        return value;
    }

    const record = value as Record<string, unknown>;
    for (const key of Object.keys(record)) {
        record[key] = scrubValue(record[key]);
    }
    return value;
};

const scrubSentryEvent = <T>(event: T): T => {
    const user = (event as { user?: Record<string, unknown> | null }).user;
    if (user) {
        delete user.email;
        delete user.username;
        delete user.ip_address;
    }

    scrubValue(event);
    return event;
};

export const getSentryInitMetadata = (): SentryInitMetadata => {
    const metadata: SentryInitMetadata = {
        // Explicit so a future SDK default change cannot silently enable default PII.
        sendDefaultPii: false,
        beforeSend: scrubSentryEvent,
        beforeSendTransaction: scrubSentryEvent
    };
    const dsn: string | undefined = getSentryDsn();
    const release: string | undefined = readBuildString(process.env.THEIA_SENTRY_RELEASE || process.env.SENTRY_RELEASE);
    const environment: string | undefined = readBuildString(
        process.env.THEIA_SENTRY_ENVIRONMENT || process.env.SENTRY_ENVIRONMENT
    );

    if (dsn !== undefined) metadata.dsn = dsn;
    if (release !== undefined) metadata.release = release;
    if (environment !== undefined) metadata.environment = environment;

    return metadata;
};
